import axios, { AxiosResponse } from 'axios';
import { LoginRequest, LoginResponse, User } from '../types/auth';
import { operationLogger, OperationType } from '../utils/operationLogger';

// ⚠️ 重要：API_BASE_URL 应该包含 /api 前缀
// 本地开发：http://localhost:8000/api
// 生产环境：https://your-domain.com/api
// 所有请求路径都是相对路径，不要再加 /api 前缀
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000/api';

// 创建专门用于认证的axios实例
// 注意：baseURL 已包含 /api，所以请求路径使用相对路径（如 /auth/login）
const authAxios = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authApi = {
  /**
   * 用户登录（增强版日志记录）
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    // 开始分步骤登录流程
    const processId = operationLogger.startStepProcess('用户登录', 4);
    
    try {
      // 步骤1: 验证输入参数
      operationLogger.logStep(processId, 1, '验证登录参数', {
        email,
        hasPassword: !!password,
        emailValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      }, 'start');
      
      if (!email || !password) {
        operationLogger.logStep(processId, 1, '参数验证失败', {
          missingEmail: !email,
          missingPassword: !password
        }, 'error');
        operationLogger.finishStepProcess(processId, false, '登录参数不完整');
        throw new Error('邮箱和密码不能为空');
      }
      
      operationLogger.logStep(processId, 1, '参数验证通过', null, 'success');
      
      // 步骤2: 发送登录请求
      operationLogger.logStep(processId, 2, '发送登录API请求', {
        url: '/auth/login',
        method: 'POST'
      }, 'start');
      
      operationLogger.logAuthFlow('login_request_start', {
        email,
        requestUrl: `${API_BASE_URL}/auth/login`
      }, 'start');
      
      const requestId = operationLogger.logAPIRequest('POST', '/auth/login', { email, password: '[REDACTED]' });
      const startTime = Date.now();
      
      // 请求路径：/auth/login（相对路径，baseURL已包含/api）
      // 最终URL：{baseURL}/auth/login = http://localhost:8000/api/auth/login
      const response: AxiosResponse<LoginResponse> = await authAxios.post('/auth/login', {
        email,
        password,
      });
      
      const duration = Date.now() - startTime;
       operationLogger.logAPIResponse(requestId, response.status, {
         hasToken: !!response.data.access_token,
         user: response.data.user ? {
           id: response.data.user.id,
           email: response.data.user.email
         } : null
       }, response.headers as Record<string, string>, duration);
      
      operationLogger.logStep(processId, 2, 'API请求成功', {
        status: response.status,
        duration
      }, 'success');
      
      // 步骤3: 处理响应数据
      operationLogger.logStep(processId, 3, '处理登录响应', {
        hasToken: !!response.data.access_token,
        hasUser: !!response.data.user,
        userId: response.data.user?.id
      }, 'start');
      
      operationLogger.logAuthFlow('login_response_received', {
        hasToken: !!response.data.access_token,
        tokenLength: response.data.access_token?.length,
        user: response.data.user
      }, 'success');
      
      // 步骤4: 设置用户上下文
      if (response.data.user?.id) {
        operationLogger.logStep(processId, 4, '设置用户上下文', {
          userId: response.data.user.id,
          email: response.data.user.email
        }, 'start');
        
        operationLogger.setUserId(response.data.user.id);
        
        operationLogger.logStep(processId, 4, '用户上下文设置完成', null, 'success');
      }
      
      operationLogger.logStep(processId, 3, '响应处理完成', null, 'success');
      
      operationLogger.logAuthFlow('login_complete', {
        email,
        userId: response.data.user?.id,
        duration
      }, 'success');
      
      operationLogger.finishStepProcess(processId, true, `用户 ${email} 登录成功`);
      
      return response.data;
    } catch (error: any) {
      const duration = Date.now();
      
      operationLogger.logAuthFlow('login_failed', {
        email,
        error: {
          message: error.message,
          status: error?.response?.status,
          data: error?.response?.data
        }
      }, 'error');
      
      operationLogger.logAPIError('login_request', error, duration);
      
      operationLogger.finishStepProcess(processId, false, `登录失败: ${error.message}`);
      
      throw error;
    }
  },


  /**
   * 获取当前用户信息
   */
  async getCurrentUser(token: string): Promise<User> {
    const response: AxiosResponse<User> = await authAxios.get('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  /**
   * 用户登出
   */
  async logout(token: string): Promise<void> {
    operationLogger.info(OperationType.AUTH_LOGOUT, '开始用户登出', {
      timestamp: new Date().toISOString()
    });
    
    try {
      await authAxios.post('/auth/logout', {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      operationLogger.success(OperationType.AUTH_LOGOUT, '用户登出成功', {
        timestamp: new Date().toISOString()
      });
    } catch (error: any) {
      operationLogger.error(OperationType.AUTH_LOGOUT, '用户登出失败', {
        error: error?.response?.data || error?.message || error,
        status: error?.response?.status,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },

  /**
   * 检查认证状态
   */
  async checkAuthStatus(token: string): Promise<{ authenticated: boolean }> {
    operationLogger.debug(OperationType.AUTH_CHECK, '检查认证状态', {
      hasToken: !!token,
      timestamp: new Date().toISOString()
    });
    
    try {
      const response: AxiosResponse<{ authenticated: boolean }> = await authAxios.get('/auth/status', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      operationLogger.debug(OperationType.AUTH_CHECK, `认证状态检查完成: ${response.data.authenticated ? '已认证' : '未认证'}`, {
        authenticated: response.data.authenticated,
        timestamp: new Date().toISOString()
      });
      
      return response.data;
    } catch (error: any) {
      operationLogger.warn(OperationType.AUTH_CHECK, '认证状态检查失败', {
        error: error?.response?.data || error?.message || error,
        status: error?.response?.status,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },

  /**
   * 密码重置请求
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await authAxios.post('/auth/reset-password', {
      email,
    });
    return response.data;
  },

  /**
   * 确认密码重置
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await authAxios.post('/auth/reset-password/confirm', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },
};